import { httpClient } from '@/configs/axios.config'
import type { components } from '@/types/api.generated'
import type {
  Company,
  CompanyList,
  CompanySearch,
  CreateCompany,
  UpdateCompany,
  CompanyIndustry,
  CompanySector,
  FundBadge,
} from '@/types/api'
import {
  buildCompanyActualVsBudgetRequest,
  OVERVIEW_ACTUAL_VS_BUDGET_METRICS,
  type CompanyActualVsBudgetMetric,
  type CompanyActualVsBudgetResponse,
} from '@/features/company-overview/actual-vs-budget'

export type CompanyTransaction =
  components['schemas']['CompanyTransactionResponseDto']
export type CompanyTransactionsResponse =
  components['schemas']['CompanyTransactionsListResponseDto']
export type CompanyTransactionType = CompanyTransaction['type']

export type GetCompanyTransactionsParams = {
  type?: CompanyTransactionType
  fundId?: string
  limit?: number
}

export type CompanyFinancialsScenarioType = 'ACTUAL' | 'BUDGET'
export type CompanyFinancialsView = 'MONTHLY' | 'QUARTERLY' | 'ANNUAL'
export type CompanyFinancialsTrailingPeriod =
  | 'LTM'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'YTD'

export type CompanyFinancialsTableMeta = {
  companyId: string
  scenarioType: CompanyFinancialsScenarioType
  view: CompanyFinancialsView
  trailingPeriod: CompanyFinancialsTrailingPeriod
  asOfPeriodEndDate: string
  updatedAt?: string
}

export type CompanyFinancialsTableColumn = {
  key: string
  label: string
}

export type CompanyFinancialsTableRow = {
  key: string
  label: string
  sortOrder: number
  metricTypeCode?: string | null
  metricTypeName?: string | null
  metricCategory?: string | null
  dataType: 'CURRENCY' | 'PERCENTAGE' | 'NUMBER' | 'RATIO'
  globalSectionId?: number | null
  globalSectionName?: string | null
  values: Record<string, number>
}

export type CompanyFinancialsTableResponse = {
  meta: CompanyFinancialsTableMeta
  columns: CompanyFinancialsTableColumn[]
  rows: CompanyFinancialsTableRow[]
}

/**
 * Search companies with filters, sorts, and pagination
 * Supports optional viewId to apply saved view configuration
 *
 * Note: Filter field name transformation (e.g., primaryOwner -> primaryOwner.name)
 * is now handled by the backend via COMPANY_FIELD_TO_FILTER_PATH mapping.
 */
export async function searchCompanies(
  params: CompanySearch
): Promise<CompanyList> {
  const response = await httpClient.post<CompanyList>(
    '/companies/search',
    params
  )
  return response.data
}

/**
 * Get a single company by ID
 */
export async function getCompany(id: string): Promise<Company> {
  const response = await httpClient.get<Company>(`/companies/${id}`)
  return response.data
}

/**
 * Get GS-34's display-ready FYTD Revenue comparison for the Overview pilot.
 * The frontend must not recompute matching, fiscal windows, or variance.
 */
export async function getCompanyActualVsBudget(
  companyId: string,
  metrics: readonly CompanyActualVsBudgetMetric[] = OVERVIEW_ACTUAL_VS_BUDGET_METRICS
): Promise<CompanyActualVsBudgetResponse> {
  const request = buildCompanyActualVsBudgetRequest(companyId, metrics)
  const response = await httpClient.get<CompanyActualVsBudgetResponse>(
    request.url,
    { params: request.params }
  )
  return response.data
}

/**
 * Get the most recent transactions for a company.
 * Defaults to all types; pass `type: 'EQUITY_INVESTMENT', limit: 5` for the overview register.
 */
export async function getCompanyTransactions(
  companyId: string,
  params: GetCompanyTransactionsParams = {}
): Promise<CompanyTransactionsResponse> {
  const response = await httpClient.get<CompanyTransactionsResponse>(
    `/companies/${companyId}/transactions`,
    { params }
  )
  return response.data
}

export async function getCompanyFinancialsTable(
  companyId: string,
  params: {
    scenarioType?: CompanyFinancialsScenarioType
    view?: CompanyFinancialsView
    trailingPeriod?: CompanyFinancialsTrailingPeriod
    showBlankPeriods?: boolean
    asOfDate?: string
    months?: number
    quarters?: number
    years?: number
  } = {}
): Promise<CompanyFinancialsTableResponse> {
  const response = await httpClient.get<CompanyFinancialsTableResponse>(
    `/companies/${companyId}/financials`,
    { params }
  )
  return response.data
}

/**
 * Create a new company
 */
export async function createCompany(data: CreateCompany): Promise<Company> {
  const response = await httpClient.post<Company>('/companies', data)
  return response.data
}

/**
 * Update an existing company (PATCH accepts partial data)
 */
export async function updateCompany(
  id: string,
  data: Partial<UpdateCompany>
): Promise<Company> {
  const response = await httpClient.patch<Company>(`/companies/${id}`, data)
  return response.data
}

/**
 * Delete a company
 */
export async function deleteCompany(id: string): Promise<void> {
  await httpClient.delete(`/companies/${id}`)
}

export interface UpdateCompanyFundInput {
  irr?: number | null
  ownershipPercentage?: number | null
  investmentStatus?: 'ACTIVE' | 'EXITED' | null
}

export async function updateCompanyFund(
  companyId: string,
  fundId: string,
  data: UpdateCompanyFundInput
): Promise<FundBadge> {
  const response = await httpClient.patch<FundBadge>(
    `/companies/${companyId}/funds/${fundId}`,
    data
  )
  return response.data
}

// =============================================================================
// Valuation History
// =============================================================================

export type ValuationHistoryRange = 'LTM' | 'ALL'

export type ValuationHistoryFund = {
  fundId: string
  abbreviation: string
  value: number | null
  sourceDate: string | null
}

export type ValuationHistoryPeriod = {
  periodEnd: string
  label: string
  funds: ValuationHistoryFund[]
  total: { value: number | null }
}

export type ValuationHistoryResponse = {
  range: ValuationHistoryRange
  periods: ValuationHistoryPeriod[]
}

export async function getCompanyValuationHistory(
  companyId: string,
  params: { range?: ValuationHistoryRange } = {}
): Promise<ValuationHistoryResponse> {
  const response = await httpClient.get<ValuationHistoryResponse>(
    `/companies/${companyId}/valuation-history`,
    { params }
  )
  return response.data
}

// =============================================================================
// Company Industry Relationships
// =============================================================================

/**
 * Add an industry to a company
 */
export async function addCompanyIndustry(
  companyId: string,
  industryId: string
): Promise<CompanyIndustry> {
  const response = await httpClient.post<CompanyIndustry>(
    `/companies/${companyId}/industries`,
    { industryId }
  )
  return response.data
}

/**
 * Remove an industry from a company
 */
export async function removeCompanyIndustry(
  companyId: string,
  industryId: string
): Promise<void> {
  await httpClient.delete(`/companies/${companyId}/industries/${industryId}`)
}

// =============================================================================
// Company Sector Relationships
// =============================================================================

/**
 * Add a sector to a company
 */
export async function addCompanySector(
  companyId: string,
  sectorId: string
): Promise<CompanySector> {
  const response = await httpClient.post<CompanySector>(
    `/companies/${companyId}/sectors`,
    { sectorId }
  )
  return response.data
}

/**
 * Remove a sector from a company
 */
export async function removeCompanySector(
  companyId: string,
  sectorId: string
): Promise<void> {
  await httpClient.delete(`/companies/${companyId}/sectors/${sectorId}`)
}
